import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Grid,
  Chip
} from '@mui/material';
import {
  X,
  Tv,
  FileText,
  Download,
  ExternalLink,
  Laptop,
  CheckCircle2,
  Sliders
} from 'lucide-react';

export default function DualScreenTutorialDialog({ open, onClose }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: '#0f081d !important',
          backgroundColor: '#0f081d !important',
          backdropFilter: 'none !important',
          border: '1px solid rgba(0, 240, 255, 0.35)',
          borderRadius: 3,
          boxShadow: '0 25px 70px rgba(0, 0, 0, 0.98), 0 0 40px rgba(0, 240, 255, 0.25)',
          color: '#fff',
          overflow: 'hidden'
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
          pb: 2,
          pt: 2.5,
          px: 3,
          mb: 2,
          background: '#150a26'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.2), rgba(112, 0, 255, 0.2))',
              border: '1px solid rgba(0, 240, 255, 0.4)'
            }}
          >
            <Tv size={22} color="#00f0ff" />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '0.5px', color: '#fff', fontSize: '1.2rem' }}>
              Tutorial de Conexión a Pantalla Extendida
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(0, 240, 255, 0.8)', fontWeight: 500 }}>
              Configuración óptima para karaoke en TV, Proyector o Segunda Pantalla
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'rgba(255, 255, 255, 0.6)',
            '&:hover': { color: '#ff0055', transform: 'rotate(90deg)', transition: 'all 0.25s ease' }
          }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      {/* Content with customized scrollbar */}
      <DialogContent
        sx={{
          py: 3,
          px: 3,
          maxHeight: '65vh',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px'
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '10px'
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 240, 255, 0.3)',
            borderRadius: '10px',
            '&:hover': {
              background: 'rgba(0, 240, 255, 0.6)'
            }
          }
        }}
      >
        {/* Concept Banner */}
        <Box
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 2,
            background: 'rgba(0, 240, 255, 0.05)',
            border: '1px solid rgba(0, 240, 255, 0.2)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            gap: 2
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#00f0ff', mb: 0.5 }}>
              ¿Cómo funciona la doble pantalla en Garaoke?
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.6 }}>
              Tu computadora actúa como el <strong>Puesto de Control (DJ / Anfitrión)</strong>, donde gestionas la música, buscas canciones y lanzas aplausos. La pantalla secundaria (TV o Proyector) muestra únicamente el <strong>Video del Karaoke en pantalla completa</strong> con la letra sincronizada para los cantantes.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Chip icon={<Laptop size={14} color="#00f0ff" />} label="Controlador" size="small" sx={{ bgcolor: 'rgba(0, 240, 255, 0.15)', color: '#00f0ff' }} />
            <Chip icon={<Tv size={14} color="#a855f7" />} label="Proyector / TV" size="small" sx={{ bgcolor: 'rgba(168, 85, 247, 0.15)', color: '#c084fc' }} />
          </Box>
        </Box>

        {/* Steps */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {/* Step 1 */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box
              sx={{
                p: 2,
                height: '100%',
                borderRadius: 2,
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: 'rgba(0, 240, 255, 0.2)',
                    color: '#00f0ff',
                    fontWeight: 800,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.85rem'
                  }}
                >
                  1
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fff' }}>
                  Conecta el Cable
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.84rem', lineHeight: 1.5 }}>
                Conecta tu notebook o PC a la TV o proyector mediante un cable <strong>HDMI</strong>, DisplayPort o adaptador USB-C a HDMI.
              </Typography>
            </Box>
          </Grid>

          {/* Step 2 */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box
              sx={{
                p: 2,
                height: '100%',
                borderRadius: 2,
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: 'rgba(168, 85, 247, 0.2)',
                    color: '#c084fc',
                    fontWeight: 800,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.85rem'
                  }}
                >
                  2
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fff' }}>
                  Extiende tu Pantalla
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.84rem', lineHeight: 1.5 }}>
                En Windows presiona <strong>Win + P</strong> y elige <strong>"Extender"</strong> (no "Duplicar"). En Mac ve a <em>Ajustes &gt; Pantallas &gt; Extender</em>.
              </Typography>
            </Box>
          </Grid>

          {/* Step 3 */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box
              sx={{
                p: 2,
                height: '100%',
                borderRadius: 2,
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: 'rgba(0, 240, 120, 0.2)',
                    color: '#00f078',
                    fontWeight: 800,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.85rem'
                  }}
                >
                  3
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fff' }}>
                  Lanza el Karaoke
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.84rem', lineHeight: 1.5 }}>
                Haz clic en el botón superior <strong>"Abrir Pantalla Karaoke"</strong>. Arrastra esa ventana a tu TV y presiona <strong>F11</strong> para pantalla completa.
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Schematic Preview */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 2,
            background: 'rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            textAlign: 'center'
          }}
        >
          <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: 'rgba(255, 255, 255, 0.6)', fontWeight: 600 }}>
            ESQUEMA VISUAL DE INSTALACIÓN
          </Typography>
          <Box
            component="img"
            src="/docs/garaoke_connection_schematic.png"
            alt="Esquema de conexión de pantalla extendida Garaoke"
            sx={{
              maxWidth: '100%',
              maxHeight: 260,
              objectFit: 'contain',
              borderRadius: 1.5,
              boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </Box>

        {/* Action Downloads Banner */}
        <Box
          sx={{
            p: 2.5,
            borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.08) 0%, rgba(112, 0, 255, 0.12) 100%)',
            border: '1px solid rgba(0, 240, 255, 0.25)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fff' }}>
              Material descargable para eventos e instalaciones
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Accede a la presentación completa en diapositivas (.PPTX) o manual imprimible en formato PDF.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              component="a"
              href="/docs/Dual_Screen_Setup.pdf"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<ExternalLink size={16} />}
              sx={{
                background: 'rgba(0, 240, 255, 0.2)',
                border: '1px solid rgba(0, 240, 255, 0.4)',
                color: '#00f0ff',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.82rem',
                '&:hover': {
                  background: 'rgba(0, 240, 255, 0.35)',
                  boxShadow: '0 0 15px rgba(0, 240, 255, 0.4)'
                }
              }}
            >
              Ver PDF
            </Button>
            <Button
              variant="contained"
              component="a"
              href="/docs/Dual_Screen_Setup.pdf"
              download="Garaoke_Dual_Screen_Setup.pdf"
              startIcon={<Download size={16} />}
              sx={{
                background: 'linear-gradient(135deg, #00f0ff 0%, #0099ff 100%)',
                color: '#0a0d18',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.82rem',
                boxShadow: '0 0 15px rgba(0, 240, 255, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #33f3ff 0%, #1ab2ff 100%)',
                  boxShadow: '0 0 20px rgba(0, 240, 255, 0.6)'
                }
              }}
            >
              Descargar PDF
            </Button>
            <Button
              variant="outlined"
              component="a"
              href="/docs/Dual_Screen_Setup.pptx"
              download="Garaoke_Dual_Screen_Setup.pptx"
              startIcon={<Download size={16} />}
              sx={{
                borderColor: 'rgba(168, 85, 247, 0.5)',
                color: '#c084fc',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.82rem',
                '&:hover': {
                  borderColor: '#a855f7',
                  background: 'rgba(168, 85, 247, 0.15)'
                }
              }}
            >
              Descargar PPTX
            </Button>
          </Box>
        </Box>
      </DialogContent>

      {/* Footer */}
      <DialogActions
        sx={{
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          py: 1.8,
          px: 3,
          background: 'rgba(10, 13, 22, 0.6)'
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            color: 'rgba(255, 255, 255, 0.8)',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { color: '#fff', background: 'rgba(255, 255, 255, 0.06)' }
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
